/**
 * useIdleHide — generic idle-detection primitive for auto-hiding surfaces.
 *
 * Returns `true` while the user is active, `false` after `idleMs` of no
 * input events on the document. Active events: mousemove, touchstart,
 * keydown, scroll, focusin. Visibility flips back to `true` immediately
 * on any active event.
 *
 * Designed for the Header auto-hide (`components/layout/Header.tsx`).
 * The ChatPage body-overflow lock doesn't interfere — `scroll` events
 * still fire from the inner `custom-scrollbar` container, which
 * propagates a scroll event up to the document.
 *
 * Reusable: any surface that wants idle-aware behavior (e.g. command
 * palette dim, toast auto-clear) can call this with its own threshold.
 */
import { useEffect, useState } from 'react';

export function useIdleHide(idleMs: number = 4000): boolean {
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const resetTimer = () => {
      setIsActive(true);
      if (timer !== null) {clearTimeout(timer);}
      timer = setTimeout(() => setIsActive(false), idleMs);
    };

    const events: (keyof DocumentEventMap)[] = [
      'mousemove',
      'touchstart',
      'keydown',
      'scroll',
      'focusin',
    ];
    events.forEach((e) =>
      document.addEventListener(e, resetTimer, { passive: true })
    );
    // Start the initial idle countdown so the surface retracts on first load.
    resetTimer();

    return () => {
      if (timer !== null) {clearTimeout(timer);}
      events.forEach((e) => document.removeEventListener(e, resetTimer));
    };
  }, [idleMs]);

  return isActive;
}
